import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { css } from "@emotion/css";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { Area } from "types";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import LessonMetadataSection from "../components/LessonMetadataSection";
import { LessonSaveButton, LessonValidationIssues } from "../components/LessonSaveControls";
import PartsSection from "../components/PartsSection";
import PreviewColumn from "../components/preview/PreviewColumn";
import { useSaveLesson } from "routes/createContent/api/useSaveLesson";
import { useUpdateLessonState } from "routes/createContent/api/useUpdateLessonState";
import { useDeleteLesson } from "routes/createContent/api/useDeleteLesson";
import { LessonFormData } from "../types";
import { LOCALES, LessonState } from "../constants";
import { getErrorMessage } from "../utils";
import { useActiveLocale } from "../ActiveLocaleContext";

type Props = {
  initialValues: LessonFormData;
  /** Called after a successful save of an existing lesson so the parent can
   *  remount the editor against the freshly-refetched server state. */
  onSaved?: () => void;
};

const LessonEditor: React.VFC<Props> = ({ initialValues, onSaved }) => {
  const { t } = useTranslation();
  const history = useHistory();
  // shouldUnregister: false is REQUIRED — in RHF v6 the default is `true`, which
  // causes getValues() to return only the output of registered Controllers,
  // discarding anything carried in shallow field state. We deliberately keep
  // part.id and translationIds out of Controllers (they're metadata, not
  // inputs), so without shallow merging they'd be stripped from every save
  // payload and the backend would treat every save as a create.
  const form = useForm<LessonFormData>({ defaultValues: initialValues, shouldUnregister: false });
  const save = useSaveLesson();
  const setState = useUpdateLessonState();
  const del = useDeleteLesson();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEditing = initialValues.id != null;
  const { activeLocale, setActiveLocale } = useActiveLocale();
  const { register } = form;

  React.useEffect(() => {
    register("id");
  }, [register]);

  const onSubmit = form.handleSubmit(async () => {
    const data = form.getValues();
    if (initialValues.creator_id != null) {
      if (!window.confirm(t("creator.confirm_lesson"))) return;
    }
    try {
      const res = await save.mutateAsync(data);
      if (!isEditing) {
        // LessonEditorPage will refetch and remount the editor with the fresh
        // saved data once the URL changes.
        history.replace(Area.Lessons_Edit.replace(":id", String(res.id)));
        return;
      }
      // Ask the parent to remount with the refetched server state. This is the
      // only reliable way to sync all of RHF's nested useFieldArray instances
      // (parts → content blocks → questions → answers) with the post-save
      // values: setValue/reset on the top-level `parts` path doesn't propagate
      // to the nested field arrays in RHF v6, so newly-added blocks would
      // otherwise disappear until a manual page reload.
      onSaved?.();
    } catch {
      // On partial failure, keep any ids useSaveLesson already assigned so a
      // retry doesn't re-POST the same parts/translations.
      form.reset(data);
    }
  });

  const onChangeState = (state: LessonState) => {
    if (!initialValues.id) return;
    setState.mutate(
      { id: initialValues.id, state },
      { onSuccess: () => form.setValue("state", state, { shouldDirty: false }) }
    );
  };

  const onDelete = () => {
    if (!initialValues.id) return;
    del.mutate(initialValues.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        history.push(Area.Lessons);
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit}>
        {/* Top bar: title area + locale selector */}
        <Box className={styles.topBar}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Editing locale</InputLabel>
            <Select
              label="Editing locale"
              value={activeLocale}
              onChange={e => setActiveLocale(e.target.value as string)}
            >
              {LOCALES.map(l => (
                <MenuItem key={l.code} value={l.code}>
                  {l.flag} {l.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={styles.layout}>
          <Box className={styles.editorColumn}>
            <LessonMetadataSection
              isEditing={isEditing}
              onChangeState={onChangeState}
              disabled={setState.isLoading || del.isLoading}
            />
            <PartsSection />
            <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 16 }}>
              <LessonSaveButton isEditing={isEditing} saving={save.isLoading} />
              <Button variant="outlined" component={Link} to={Area.Lessons}>
                Back to list
              </Button>
              {isEditing && (
                <Box ml="auto">
                  <Button
                    variant="outlined"
                    // Inline red avoids the MUI v4 theme bug where
                    // color="secondary" renders the outlined button invisible
                    // in this app's palette.
                    style={{ color: "#f44336", borderColor: "#f44336" }}
                    onClick={() => {
                      del.reset();
                      setConfirmDelete(true);
                    }}
                    disabled={del.isLoading}
                  >
                    Delete lesson
                  </Button>
                </Box>
              )}
            </Box>
            <LessonValidationIssues />
            {save.isError && (
              <Box mt={1} color="error.main">
                {save.error ? getErrorMessage(save.error) : "Failed to save"}
              </Box>
            )}
            {del.isError && (
              <Box mt={1} color="error.main">
                {getErrorMessage(del.error)}
              </Box>
            )}
            {setState.isError && (
              <Box mt={1} color="error.main">
                {getErrorMessage(setState.error)}
              </Box>
            )}
          </Box>
          <Box className={styles.previewColumn}>
            <PreviewColumn />
          </Box>
        </Box>
      </form>
      <ConfirmDeleteDialog
        open={confirmDelete}
        loading={del.isLoading}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </FormProvider>
  );
};

export default LessonEditor;

const styles = {
  topBar: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 12px 16px 0;
  `,
  layout: css`
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    padding: 16px;
    align-items: start;
  `,
  editorColumn: css`
    min-width: 0;
  `,
  previewColumn: css`
    position: sticky;
    top: 16px;
  `,
};
